import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  consignmentApi,
  type ConsignmentNode,
  type ConsignmentPositionLine,
} from '@/api/services';

/**
 * Raising a call-off.
 *
 * A dialog rather than a page or a sheet: this is an action with a decision at
 * the end of it, not a record somebody comes back to read.
 *
 * The quantity field is bounded by what is actually free at the node — what is
 * there, less what the customer already owns and what is reserved. Showing the
 * ceiling beside the field is the point: a requester who cannot see it asks for
 * what they need and finds out it was not there only after somebody approves.
 * The server checks the same thing again at fulfilment, because stock moves
 * between the two and only the transaction can hold the line.
 */
export function RaiseCallOffDialog({
  nodes,
  defaultNodeId,
  onRaised,
}: {
  nodes: ConsignmentNode[];
  defaultNodeId?: string | null;
  onRaised: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [nodeId, setNodeId] = useState<string | undefined>(defaultNodeId ?? undefined);
  const [available, setAvailable] = useState<ConsignmentPositionLine[]>([]);
  const [loadingLines, setLoadingLines] = useState(false);
  const [rows, setRows] = useState<{ itemId: string; quantity: string }[]>([
    { itemId: '', quantity: '' },
  ]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNodeId(defaultNodeId ?? nodes[0]?.id);
  }, [open, defaultNodeId, nodes]);

  useEffect(() => {
    if (!open || !nodeId) return;
    setLoadingLines(true);
    // Reset the lines: an item free at one node may not exist at another, and
    // carrying the selection across would submit a line that cannot be filled.
    setRows([{ itemId: '', quantity: '' }]);
    consignmentApi
      .positions(nodeId)
      .then((p) => setAvailable(p.lines.filter((l) => l.availableToCallOff > 0)))
      .catch(() => toast.error('Could not load what is held at that node'))
      .finally(() => setLoadingLines(false));
  }, [open, nodeId]);

  const byItem = useMemo(() => new Map(available.map((l) => [l.itemId, l])), [available]);

  const problems = useMemo(() => {
    const found: string[] = [];
    const filled = rows.filter((r) => r.itemId);
    if (!filled.length) found.push('Add at least one line.');
    const seen = new Set<string>();
    for (const row of filled) {
      if (seen.has(row.itemId)) {
        found.push(`${byItem.get(row.itemId)?.sku ?? 'That item'} is on more than one line.`);
      }
      seen.add(row.itemId);
      const qty = Number(row.quantity);
      const line = byItem.get(row.itemId);
      if (!row.quantity || !Number.isInteger(qty) || qty < 1) {
        found.push(`${line?.sku ?? 'A line'} needs a whole quantity of at least 1.`);
      } else if (line && qty > line.availableToCallOff) {
        found.push(`${line.sku}: ${qty} asked for, ${line.availableToCallOff} free.`);
      }
    }
    return found;
  }, [rows, byItem]);

  const submit = async () => {
    if (!nodeId || problems.length) return;
    setSaving(true);
    try {
      const created = await consignmentApi.create({
        locationId: nodeId,
        lines: rows
          .filter((r) => r.itemId)
          .map((r) => ({ itemId: r.itemId, quantity: Number(r.quantity) })),
        notes: notes.trim() || undefined,
      });
      toast.success(`${created.reference} raised`);
      setOpen(false);
      setRows([{ itemId: '', quantity: '' }]);
      setNotes('');
      await onRaised();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not raise the call-off';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button disabled={!nodes.length}>
          <Plus className="mr-1.5 h-4 w-4" />
          Raise call-off
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Raise a call-off</DialogTitle>
          <DialogDescription>
            The customer drawing against stock held at their site. Title passes when it is
            fulfilled, which is what makes it billable.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Node</Label>
            <Select value={nodeId} onValueChange={setNodeId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a consignment node" />
              </SelectTrigger>
              <SelectContent>
                {nodes.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    {n.code} — {n.customer?.name ?? n.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Lines</Label>
            {loadingLines ? (
              <p className="text-sm text-muted-foreground">Loading what is held there…</p>
            ) : available.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing at this node is free to call off. Everything held there is either already
                the customer&rsquo;s or reserved.
              </p>
            ) : (
              <div className="space-y-2">
                {rows.map((row, i) => {
                  const line = byItem.get(row.itemId);
                  return (
                    <div key={i} className="flex items-start gap-2">
                      <div className="flex-1">
                        <Select
                          value={row.itemId || undefined}
                          onValueChange={(v) =>
                            setRows((r) =>
                              r.map((x, xi) => (xi === i ? { ...x, itemId: v } : x))
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose an item" />
                          </SelectTrigger>
                          <SelectContent>
                            {available.map((l) => (
                              <SelectItem key={l.itemId} value={l.itemId}>
                                {l.sku} — {l.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-40">
                        <Input
                          type="number"
                          min={1}
                          max={line?.availableToCallOff}
                          placeholder="Qty"
                          value={row.quantity}
                          onChange={(e) =>
                            setRows((r) =>
                              r.map((x, xi) =>
                                xi === i ? { ...x, quantity: e.target.value } : x
                              )
                            )
                          }
                        />
                        {line && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {line.availableToCallOff} free{line.unit ? ` ${line.unit}` : ''}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label="Remove line"
                        disabled={rows.length === 1}
                        onClick={() => setRows((r) => r.filter((_, xi) => xi !== i))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  );
                })}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRows((r) => [...r, { itemId: '', quantity: '' }])}
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Add line
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="call-off-notes">Notes</Label>
            <Textarea
              id="call-off-notes"
              rows={2}
              placeholder="What the material is for."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {problems.length > 0 && rows.some((r) => r.itemId) && (
            // Every problem at once. Fixing them one at a time means learning
            // about the next only after resubmitting, which is how the server
            // reports them too.
            <ul className="space-y-0.5 text-xs text-status-destructive-foreground">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || problems.length > 0 || !nodeId}>
            {saving ? 'Raising…' : 'Raise call-off'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
